import React, { FC } from 'react';
import cn from 'classnames';
import { ReactComponent as MarkerIcon } from '@assets/markerWhite.svg';
import { bookingLink } from '@helpers/endpoints';
import pluralize, { PLURAL_UNITS } from '@helpers/pluralize';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';

import {
  applyFilters,
  changeFilter,
  onSelectMfc,
  openMobileMap,
  resetFilters,
  selectNearestMfc,
  toggleLegend,
  onRoutesClear,
} from '@store/actions';
import useDeviceType from '@hooks/useDeviceType';
import { findNearestOffice } from '@helpers/findNearestOffice';
import st from './Service.module.css';

type ServiceProps = {
  fullInfoVisible: boolean;
  legendOpened: boolean;
  mobileMapOpened: boolean;
  serviceId: number;
  office?: any;
  name: string;
  officesIds: [number];
  onClose: () => void;
  onFullInfoShow: (serviceId: number) => void;
  catalogWithNearestOffices?: any;

  applyFilters: () => any;
  changeFilter: (payload?: any) => any;
  onSelectMfc: (payload?: any) => any;
  openMobileMap: (payload?: any) => any;
  resetFilters: () => any;
  selectNearestMfc: (payload?: any) => any;
  toggleLegend: (payload?: any) => any;
  handlesRoutesClear: () => any;
};

const Service: FC<ServiceProps> = ({
  fullInfoVisible,
  office = null,
  name,
  serviceId,
  officesIds,
  onClose,
  onFullInfoShow,
  legendOpened,
  mobileMapOpened,
  catalogWithNearestOffices = null,
  applyFilters,
  changeFilter,
  onSelectMfc,
  openMobileMap,
  resetFilters,
  selectNearestMfc,
  toggleLegend,
  handlesRoutesClear,
}) => {
  const { phone } = useDeviceType();
  const { id: officeId, bookableServicesIds = [] } = office || {};
  const bookable = bookableServicesIds.includes(serviceId);
  // @ts-ignore
  const hasOffices = officesIds.length !== 0;
  const serviceLink = `${bookingLink}?service=${serviceId}&office=${officeId}`;
  const officeNotIncluded = !!office && !officesIds.includes(office.id);
  const history = useHistory();

  const onOfficesShow = (withReset: boolean) => {
    if (withReset) {
      resetFilters();
    }
    changeFilter({
      fieldName: 'services',
      id: serviceId,
      enabled: true,
    });
    onSelectMfc(null);
    applyFilters();
    if (phone && !legendOpened) {
      toggleLegend();
    }
    if (phone && !mobileMapOpened) {
      openMobileMap();
    }
    if (window && !phone) {
      const mw = document.getElementById('MFC-Map_Wrapper');

      setTimeout(() => mw.scrollIntoView(true), 0);
    }
    onClose();
  };

  const onNearestShow = () => {
    resetFilters();
    const nearestOffice = findNearestOffice(
      serviceId,
      officeId,
      catalogWithNearestOffices
    );
    selectNearestMfc(nearestOffice);
    if (phone && legendOpened) {
      toggleLegend();
    }
    if (phone && !mobileMapOpened) {
      openMobileMap();
    }
    if (window && !phone) {
      const mw = document.getElementById('MFC-Map_Wrapper');

      setTimeout(() => mw.scrollIntoView(true), 0);
    }
    onClose();
  };

  const handleOfficesShow = () => {
    onOfficesShow(true);
    handlesRoutesClear();
    history.push('/services/centry-gosudarstvennyh-uslug/');
  };
  const handleShowNotIncludedService = () => {
    handleOfficesShow();
  };

  const handleShowNearestOffice = () => {
    onNearestShow();
    history.push('/services/centry-gosudarstvennyh-uslug/');
  };

  const nearestMfcButton = (
    <button
      className={st.button}
      type="button"
      disabled={!hasOffices}
      onClick={handleShowNearestOffice}
    >
      Ближайший МФЦ
    </button>
  );

  // запрос mfccatalog/:id => поиск услуги => взятие 1 и 2 элемента из массива МФЦ => селектим 1 или 2 id

  const availableInButton = (
    <button
      className={st.invertedButton}
      type="button"
      disabled={!hasOffices}
      onClick={handleShowNotIncludedService}
    >
      {`Доступно в ${officesIds.length} ${pluralize(
        officesIds.length,
        PLURAL_UNITS.MFC
      )}`}
      <MarkerIcon className={st.markerIconInverted} />
    </button>
  );

  const bookTimeButton = (
    <a className={st.button} href={serviceLink}>
      Выбрать время
    </a>
  );

  const showOfficesButton = (
    <button
      className={st.button}
      type="button"
      disabled={!hasOffices}
      data-testid="catalogServiceButton"
      onClick={handleOfficesShow}
    >
      {hasOffices
        ? `Доступно в ${officesIds.length} ${pluralize(
            officesIds.length,
            PLURAL_UNITS.MFC
          )}`
        : 'Нет доступных центров'}
      {hasOffices && <MarkerIcon className={st.markerIcon} />}
    </button>
  );

  return (
    <div className={st.service}>
      <button
        data-testid="catalogToggleServiceButton"
        className={cn(st.toggle, {
          [st.toggleOpened]: fullInfoVisible,
        })}
        type="button"
        onClick={() => onFullInfoShow(serviceId)}
      >
        {officeId && (
          <small className={st.type}>
            {bookable ? 'по записи' : 'в порядке очереди'}
          </small>
        )}
        {name}
      </button>
      {fullInfoVisible && (
        <div className={st.footer}>
          {officeId && bookable && bookTimeButton}
          {!officeId && showOfficesButton}
          {officeId && officeNotIncluded && (
            <p className={st.note}>
              Услуга недоступна в выбранном МФЦ, посмотрите центры, где можно
              получить услугу
            </p>
          )}
          {officeId && officeNotIncluded && (
            <div className={st.serviceButtonsIfNotIncluded}>
              {nearestMfcButton}
              {availableInButton}
            </div>
          )}
          {officeId &&
            !officeNotIncluded &&
            (bookable ? (
              <p className={st.note}>
                Услуга&nbsp;доступна
                <br />
                по&nbsp;предварительной записи
              </p>
            ) : (
              <p className={st.note}>
                Услуга доступна без&nbsp;записи и&nbsp;оказывается
                в&nbsp;порядке электронной&nbsp;очереди
              </p>
            ))}
          {!officeId && !hasOffices && (
            <p className={st.note}>
              Попробуйте выбрать другой район или станцию метро
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  legendOpened: state.ui && state.ui.legendOpened,
  mobileMapOpened: state.ui && state.ui.mobileMapOpened,
  catalogWithNearestOffices:
    state.servicesForOffice && state.servicesForOffice.data,
});

const mapDispatchToProps = (dispatch) => ({
  applyFilters: () => dispatch(applyFilters()),
  changeFilter: (payload) => dispatch(changeFilter(payload)),
  onSelectMfc: (payload) => dispatch(onSelectMfc(payload)),

  resetFilters: () => dispatch(resetFilters()),
  openMobileMap: () => dispatch(openMobileMap()),
  toggleLegend: (payload) => dispatch(toggleLegend(payload)),
  selectNearestMfc: (payload) => dispatch(selectNearestMfc(payload)),
  handlesRoutesClear: () => dispatch(onRoutesClear()),
});

// @ts-ignore
export default connect(mapStateToProps, mapDispatchToProps)(Service);
